import { FC, useId } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { DateInputField } from '../../Inputs';
import { Row, Visible } from '../../LayoutComponents';
import { ExpandedSearchButtons } from '../../Search';
import {
  PriorityFilter,
  TransportationModeFilter,
} from '../../Search/ExtraFilters';
import { ExtraFiltersToggle, SearchQueryFilter } from '../../Search/SearchBar';
import { useToggle } from '../../Utils/useToggle';
import { LineTypeFilter } from './Filters/LineTypeFilter';
import { routesAndLinesTestIds } from './routesAndLinesTestIds';
import { RoutesAndLinesSearchFilters, SearchNavigationState } from './Types';
import { useSearch } from './useSearch';
import {
  mapFiltersToSearchConditions,
  mapSearchConditionsToFilters,
} from './Utils';

function generateNavigationState(isExpanded: boolean): SearchNavigationState {
  return {
    searchExpanded: isExpanded,
  };
}

type SearchContainerProps = {
  readonly searchExpandChanged?: (val: boolean) => void;
};

export const SearchContainer: FC<SearchContainerProps> = ({
  searchExpandChanged,
}) => {
  const { searchConditions, handleSearch } = useSearch();
  const { t } = useTranslation();
  const location = useLocation();
  const extraFiltersId = useId();

  const methods = useForm<RoutesAndLinesSearchFilters>({
    defaultValues: mapSearchConditionsToFilters(searchConditions),
  });

  const [isExpanded, toggleIsExpanded] = useToggle(
    location.state?.searchExpanded,
  );

  const toggleExpand = () => {
    searchExpandChanged?.(!isExpanded);
    toggleIsExpanded();
  };

  const onSubmit = (nextFilters: RoutesAndLinesSearchFilters) => {
    const combinedFilters = mapFiltersToSearchConditions({
      ...searchConditions,
      ...nextFilters,
    });

    handleSearch(combinedFilters, generateNavigationState(isExpanded));
  };

  return (
    <FormProvider {...methods}>
      <form
        className="container mx-auto flex flex-col py-10"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Row className="justify-center gap-x-4 bg-background py-4">
          <DateInputField<RoutesAndLinesSearchFilters>
            className="min-w-40"
            inputClassName="grow"
            fieldPath="observationDate"
            testId={routesAndLinesTestIds.observationDateInput}
            translationPrefix="filters"
          />

          <SearchQueryFilter<RoutesAndLinesSearchFilters>
            testIdPrefix={routesAndLinesTestIds.prefix}
            fieldPath="query"
            translationPrefix="stopRegistrySearch.fieldLabels"
            className="w-4/6 xl:w-2/6"
          />

          <ExtraFiltersToggle
            className="mt-[25px]"
            extraFiltersId={extraFiltersId}
            searchIsExpanded={isExpanded}
            toggleSearchIsExpanded={toggleExpand}
            testIdPrefix={routesAndLinesTestIds.prefix}
          />
        </Row>

        <Visible visible={isExpanded}>
          <div
            id={extraFiltersId}
            className="space-y-5 border-2 border-background p-10"
          >
            <h2>{t(($) => $.search.advancedSearchTitle)}</h2>
            <Row className="gap-4">
              <TransportationModeFilter<RoutesAndLinesSearchFilters>
                testIdPrefix={routesAndLinesTestIds.prefix}
                label={t(($) => $.lines.transportMode)}
                fieldPath="transportMode"
                className="sm:-order-2 md:order-0"
              />

              <PriorityFilter<RoutesAndLinesSearchFilters>
                testIdPrefix={routesAndLinesTestIds.prefix}
                translationPrefix="lines"
                fieldPath="priorities"
                className="mr-auto"
              />

              <LineTypeFilter testId={routesAndLinesTestIds.lineTypeDropdown} />
            </Row>
          </div>
          <ExpandedSearchButtons
            testIdPrefix="SearchContainer"
            searchButtonType="submit"
          />
        </Visible>
      </form>
    </FormProvider>
  );
};
